import { api } from '../api/client.js';
import type {
  ClientsData,
  DeliveryManagerData,
  DeliveryOverviewData,
  DeliveryRecruiterData,
  DeliveryScopeType,
  DeliveryTimeseriesData,
  Envelope,
  Grain,
  OverviewData,
  PeriodKey,
  RecruiterOffering,
  RegionAccountsData,
  RegionDetailData,
  Source,
  SpecialtiesData,
  Specialty,
  SyncMeta,
  Targeting,
  TimeseriesData,
  AlertsData,
  AuditListData,
  AuditResultsData,
  AuditRunsData,
} from '../api/types.js';
import type { UiRegion } from '../theme/tokens.js';
import type { QueryDescriptor } from './dataStore.js';

/**
 * Every cacheable request, named once.
 *
 * **This file exists so a page and the prefetcher cannot disagree about a key.** They are two
 * callers of the same data with no reason to meet: a page knows its filters, the prefetcher knows
 * a manifest. If each built its own cache key, the prefetcher would spend a minute filling
 * entries no page would ever look for, and every page would still start cold — a bug that
 * presents as "the prefetch does nothing" and is invisible in both call sites.
 *
 * A descriptor is a key plus the call that satisfies it. Keys are **canonical**: every parameter
 * that changes the response appears, in a fixed order, so two spellings of one request cannot
 * occupy two entries. Anything omitted here is a request that is not cached — account search is
 * the deliberate case, since its key is whatever someone is typing.
 */

/** The four table parameters, in fixed order, as a key fragment. */
type TableParams = {
  period: PeriodKey;
  source: Source;
  targeting: Targeting;
  specialty: Specialty;
};

const tp = (p: TableParams): string => `${p.period}|${p.source}|${p.targeting}|${p.specialty}`;

/** The four chart parameters, in fixed order. */
type ChartParams = {
  grain: Grain;
  source: Source;
  targeting: Targeting;
  specialty: Specialty;
};

const cp = (p: ChartParams): string => `${p.grain}|${p.source}|${p.targeting}|${p.specialty}`;

export const queries = {
  // ---- Meta --------------------------------------------------------------
  sync: (): QueryDescriptor<SyncMeta> => ({
    key: 'sync',
    label: 'Snapshot status',
    fetch: () => api.sync(),
  }),

  // ---- Regions -----------------------------------------------------------
  overview: (period: PeriodKey): QueryDescriptor<Envelope<OverviewData>> => ({
    key: `overview|${period}`,
    label: `Regions overview · ${period}`,
    fetch: () => api.overview(period),
  }),

  regionDetail: (regionCode: string): QueryDescriptor<Envelope<RegionDetailData>> => ({
    key: `region|${regionCode}`,
    label: `Region ${regionCode}`,
    fetch: () => api.regionDetail(regionCode),
  }),

  /**
   * The scope is part of the key but is never prefetched: it is set by picking a row or an
   * account, and there are thousands of accounts per region. Prefetching the unscoped chart
   * makes the first view instant, which is the one that always happens.
   *
   * ⚠ **Both halves of the scope are in the key.** `scopeType` alone would let a parent client
   * and a facility whose names normalise to the same string share one cache entry — the same
   * collision `client_timeseries_lookup` guards against in Mongo, arriving in the browser.
   */
  regionTimeseries: (
    regionCode: string,
    params: ChartParams & { scopeType?: 'client' | 'msp' | 'child' | null; scopeKey?: string | null },
  ): QueryDescriptor<Envelope<TimeseriesData>> => ({
    key: `regionTs|${regionCode}|${cp(params)}|${params.scopeType ?? ''}|${params.scopeKey ?? ''}`,
    label: `Chart · ${regionCode}`,
    fetch: () => api.timeseries(regionCode, params),
  }),

  /**
   * The region's three-level account list — fetched whole, so typing in the header search
   * filters without a round trip (FR-075).
   *
   * ⚠ **Deliberately absent from `manifest.ts`.** It is the largest single response in the
   * product (a few thousand nodes per region, ~11,400 children across the snapshot), the store
   * persists to `localStorage` under a 5MB quota, and one list per region would crowd out every
   * page the prefetch exists to make instant — `dataStore` would start evicting the entries the
   * manifest was filled for. It caches on first open of the search like anything else.
   */
  regionAccounts: (regionCode: string): QueryDescriptor<Envelope<RegionAccountsData>> => ({
    key: `regionAccounts|${regionCode}`,
    label: `Accounts · ${regionCode}`,
    fetch: () => api.regionAccounts(regionCode),
  }),

  regionClients: (
    regionCode: string,
    params: { period: PeriodKey; targeting: Targeting },
  ): QueryDescriptor<Envelope<ClientsData>> => ({
    key: `clients|${regionCode}|${params.period}|${params.targeting}`,
    label: `Clients · ${regionCode}`,
    fetch: () => api.clients(regionCode, params),
  }),

  // ---- Delivery ----------------------------------------------------------
  delivery: (params: TableParams): QueryDescriptor<Envelope<DeliveryOverviewData>> => ({
    key: `delivery|all|${tp(params)}`,
    label: 'Delivery · all managers',
    fetch: () => api.delivery(params),
  }),

  deliveryLeader: (
    leaderKey: string,
    params: TableParams,
  ): QueryDescriptor<Envelope<DeliveryOverviewData>> => ({
    key: `delivery|leader|${leaderKey}|${tp(params)}`,
    label: `Delivery · ${leaderKey}`,
    fetch: () => api.deliveryLeader(leaderKey, params),
  }),

  /**
   * Two or more leaders at once (FR-D07a). Not prefetched — seven leaders make 120 sets, and each
   * is one click from a page that is already warm.
   */
  deliveryLeaders: (
    setKey: string,
    params: TableParams,
  ): QueryDescriptor<Envelope<DeliveryOverviewData>> => ({
    key: `delivery|leaders|${setKey}|${tp(params)}`,
    label: `Delivery · ${setKey.split('\n').join(' + ')}`,
    fetch: () => api.deliveryLeaders(setKey, params),
  }),

  /**
   * `recruiterOffering` is in the key because it changes which rows come back — it narrows the
   * list server-side (FR-D13). The chart's `specialty` is a different parameter on a different
   * request, which is exactly why the two are named differently.
   */
  deliveryManager: (
    managerId: string | number,
    params: TableParams & { recruiterOffering: RecruiterOffering },
  ): QueryDescriptor<Envelope<DeliveryManagerData>> => ({
    key: `delivery|manager|${managerId}|${tp(params)}|${params.recruiterOffering}`,
    label: `Delivery · manager ${managerId}`,
    fetch: () => api.deliveryManager(managerId, params),
  }),

  deliveryRecruiter: (
    recruiterId: string | number,
    params: TableParams,
  ): QueryDescriptor<Envelope<DeliveryRecruiterData>> => ({
    key: `delivery|recruiter|${recruiterId}|${tp(params)}`,
    label: `Delivery · recruiter ${recruiterId}`,
    fetch: () => api.deliveryRecruiter(recruiterId, params),
  }),

  deliveryTimeseries: (params: {
    scopeType: DeliveryScopeType;
    scopeKey?: string | null;
    grain: Grain;
    source: Source;
    targeting: Targeting;
    specialty: Specialty;
  }): QueryDescriptor<Envelope<DeliveryTimeseriesData>> => ({
    key: `deliveryTs|${params.scopeType}|${params.scopeKey ?? ''}|${cp(params)}`,
    label: `Delivery chart · ${params.scopeType}`,
    fetch: () => api.deliveryTimeseries(params),
  }),

  // ---- Specialties -------------------------------------------------------
  specialties: (region?: UiRegion | null): QueryDescriptor<Envelope<SpecialtiesData>> => ({
    key: `specialties|${region ?? 'all'}`,
    label: `Specialties · ${region ?? 'all regions'}`,
    fetch: () => api.specialties(region ? { region } : {}),
  }),

  // ---- Audit (spec 007) --------------------------------------------------
  //
  // Every audit response is kept 12 hours — the owner's rule (AU-01), not the store's six. The
  // List is also fetched again once a newer run has become DUE (AU-19): the server sends
  // `clock.behindAt`, 07:30 New York time on the next weekday, so the browser never re-derives the
  // schedule. `staleWhen` only triggers the refetch; whether the staleness warning shows is the
  // server's `clock.stale` alone, so a browser clock cannot raise a warning the server would not.
  auditList: (): QueryDescriptor<Envelope<AuditListData>> => ({
    key: 'audit.list',
    label: 'Audit list',
    ttlMs: AUDIT_TTL_MS,
    fetch: () => api.auditList(),
    // Defensive: a payload without a clock is simply not behind — never a throw inside freshness.
    staleWhen: (value, fetchedAt, now) => isBehind(value?.data?.clock?.behindAt ?? null, fetchedAt, now),
  }),
  auditRuns: (
    auditId: string,
    before: string | null = null,
    limit = 10,
  ): QueryDescriptor<Envelope<AuditRunsData>> => ({
    key: `audit.runs|${auditId}|${before ?? 'latest'}|${limit}`,
    label: `Audit runs · ${auditId}`,
    ttlMs: AUDIT_TTL_MS,
    fetch: () => api.auditRuns(auditId, { ...(before ? { before } : {}), limit }),
  }),
  auditResults: (auditId: string, runDate: string): QueryDescriptor<Envelope<AuditResultsData>> => ({
    key: `audit.results|${auditId}|${runDate}`,
    label: `Audit results · ${auditId} · ${runDate}`,
    ttlMs: AUDIT_TTL_MS,
    fetch: () => api.auditResults(auditId, runDate),
  }),

  // ---- Alerts (spec 008) -------------------------------------------------
  //
  // ONE key, no parameters: the page filters this response in the browser, and the sidebar badge
  // and the header bell read its unfiltered `total` (FR-AL03). Same 12 hours and the same due-time
  // refetch as the Audit List, because it is the same audit data under the same constitution
  // conditions (FR-AL26, FR-AL59). Its envelope carries the ROSTER's snapshot id, so loading Alerts
  // never wipes another page's copy.
  auditAlerts: (): QueryDescriptor<Envelope<AlertsData>> => ({
    key: 'audit.alerts',
    label: 'Alerts',
    ttlMs: AUDIT_TTL_MS,
    fetch: () => api.auditAlerts(),
    staleWhen: (value, fetchedAt, now) => isBehind(value?.data?.clock?.behindAt ?? null, fetchedAt, now),
  }),
};

/** The owner's rule for audit responses (AU-01). */
export const AUDIT_TTL_MS = 12 * 60 * 60 * 1000;

const HOUR_MS = 60 * 60 * 1000;

/**
 * Should a kept List copy be fetched again? (AU-19, replacing AU-14's "two days old".)
 *
 * - Not before `behindAt`: no newer run is due yet, so the copy is current.
 * - Once it passes, a copy fetched **before** that moment is refetched straight away: that is the
 *   07:30 New York refresh.
 * - A copy fetched **after** it, which still names the old run because the run is late, is asked
 *   again at most once an hour, so a late run is picked up without fetching on every view.
 *
 * A copy with no `behindAt` (kept from before the field existed) is never behind; its 12 hours
 * still apply.
 */
export function isBehind(behindAt: string | null, fetchedAt: number, now = Date.now()): boolean {
  if (!behindAt) return false;
  const due = Date.parse(behindAt);
  if (Number.isNaN(due) || now < due) return false;
  return fetchedAt < due || now - fetchedAt >= HOUR_MS;
}

export type Queries = typeof queries;
